import { UserRoleEnum, UserRoleEnumZ } from '@monetize/types/app';
import { booleanFalseSchema, booleanTrueSchema } from '@monetize/types/common';
import { z } from 'zod';

// DEPRECATED - We can re-introduce once we have a valid use-case for this
export enum ExperienceConfigurationStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}

export enum ExperienceConfigurationKeys {
  accountSettings = 'accountSettings',
  contactSettings = 'contactSettings',
  invoiceSettings = 'invoiceSettings',
  quoteAndContractSettings = 'quoteAndContractSettings',
  quoteTemplateSettings = 'quoteTemplateSettings',
  tenantSettings = 'tenantSettings',
  //add new keys here in one place ⬆️
}

export const ExperienceConfigurationKeysZ = z.enum(
  Object.values(ExperienceConfigurationKeys),
);

export const ExperienceConfigurationKeysSet = new Set(
  Object.values(ExperienceConfigurationKeys) as ExperienceConfigurationKeys[],
);

export const ExperienceConfigurationBaseSchema = z.object({
  createdBy: z.string().min(1),
  createdDate: z.union([z.string(), z.date()]).nullish(),
  lastModifiedBy: z.string(),
  modifyDate: z.union([z.string(), z.date()]).nullish(),
});

export const RoleBasedSettingConfigDefaultAllSchema = z
  .array(z.union([UserRoleEnumZ, z.literal('ALL')]))
  .default(['ALL']);

export const RoleBasedSettingConfigDefaultNoneSchema = z
  .array(z.union([UserRoleEnumZ, z.literal('ALL')]))
  .default([]);

export const RoleBasedSettingConfigDefaultAdminSchema = z
  .array(z.union([UserRoleEnumZ, z.literal('ALL')]))
  .default([
    UserRoleEnum.TENANT_ADMIN,
    UserRoleEnum.SALES_ADMIN,
    UserRoleEnum.BILLING_ADMIN,
  ]);

/**
 * Role-based visibility for an individual setting (Editable / Read Only / Hidden).
 * Used by role-scoped settings such as Quote Template customizations.
 */
export enum SettingVisibilityEnum {
  EDITABLE = 'EDITABLE',
  READ_ONLY = 'READ_ONLY',
  HIDDEN = 'HIDDEN',
}

export const SettingVisibilityEnumZ = z.enum(
  Object.values(SettingVisibilityEnum),
);

export enum SettingAccessScopeEnum {
  ALL_ROLES = 'ALL_ROLES',
  SPECIFIC_ROLES = 'SPECIFIC_ROLES',
}

export const SettingAccessScopeEnumZ = z.enum(
  Object.values(SettingAccessScopeEnum),
);

/**
 * Contact Settings
 */
export const ContactSettingsConfigSchema = z.object({
  preventContactCreate: booleanFalseSchema,
  preventContactEdit: booleanFalseSchema,
});

export const ContactSettingsConfigWithKeySchema = z.object({
  key: z.literal(ExperienceConfigurationKeys.contactSettings),
  configuration: ContactSettingsConfigSchema.prefault({}),
});

/**
 * Account Settings
 */
export const AccountSettingsConfigSchema = z.object({
  preventAccountCreate: booleanFalseSchema,
  preventAccountEdit: booleanFalseSchema,
});

export const AccountSettingsConfigWithKeySchema = z.object({
  key: z.literal(ExperienceConfigurationKeys.accountSettings),
  configuration: AccountSettingsConfigSchema.prefault({}),
});

export const SettingsConfigTOCPermissionSchema = z.object({
  disableQuoteTermsAndConditions: z
    .union([z.boolean(), z.literal('SPECIFIC_ROLES')])
    .default(false),
  allowedRoles: z.array(z.string()).default([]),
});

export type ISettingsConfigTOCPermissionSchema = z.infer<
  typeof SettingsConfigTOCPermissionSchema
>;

/**
 * Visibility for a customer-editable field on the Quote Share page.
 * HIDDEN — not shown; READ_ONLY — shown but not editable; EDITABLE — shown and
 * editable; EDITABLE_REQUIRED — editable and required before a quote can be
 * accepted. Distinct from {@link SettingVisibilityEnum} (which is 3-state and
 * shared with Custom Fields).
 */
export enum QuoteShareFieldVisibilityEnum {
  HIDDEN = 'HIDDEN',
  READ_ONLY = 'READ_ONLY',
  EDITABLE = 'EDITABLE',
  EDITABLE_REQUIRED = 'EDITABLE_REQUIRED',
}

export const QuoteShareFieldVisibilityEnumZ = z.enum(
  Object.values(QuoteShareFieldVisibilityEnum),
);

/**
 * Human-readable labels for {@link QuoteShareFieldVisibilityEnum}. Shared by the
 * Quote Share settings UI and the Record Automation rule builder so the options
 * stay in sync.
 */
export const QuoteShareFieldVisibilityEnumDisplay: Record<
  QuoteShareFieldVisibilityEnum,
  string
> = {
  [QuoteShareFieldVisibilityEnum.HIDDEN]: 'Hidden',
  [QuoteShareFieldVisibilityEnum.READ_ONLY]: 'Read-Only',
  [QuoteShareFieldVisibilityEnum.EDITABLE]: 'Editable',
  [QuoteShareFieldVisibilityEnum.EDITABLE_REQUIRED]: 'Required (Editable)',
};

export const SettingsConfigQuoteShareSchema = z.object({
  enabled: booleanFalseSchema,
  esign: z
    .object({
      disableManualSend: booleanFalseSchema,
    })
    .prefault({}),
  contact: z
    .object({
      requireAddress: booleanTrueSchema,
    })
    .prefault({}),
  // Both default to EDITABLE; the fields only render on the share page when the
  // backend enables them (`allowPoNumber` / `allowVatNumber`), so the config
  // visibility refines behavior rather than acting as the primary gate.
  purchaseOrderNumber: z
    .object({
      visibility: QuoteShareFieldVisibilityEnumZ.default(
        QuoteShareFieldVisibilityEnum.EDITABLE,
      ),
    })
    .prefault({}),
  vatNumber: z
    .object({
      visibility: QuoteShareFieldVisibilityEnumZ.default(
        QuoteShareFieldVisibilityEnum.EDITABLE,
      ),
    })
    .prefault({}),
  // Whether the customer may update the account's Company Legal Name from the
  // share page. Off by default; can be enabled here and/or via Record Automation.
  accountName: z
    .object({
      allowUpdate: booleanFalseSchema,
    })
    .prefault({}),
  paymentMethod: z
    .object({
      required: booleanTrueSchema,
      manualACH: z
        .object({
          enabled: booleanFalseSchema,
          scope: z.enum(['ALL', 'LEGAL_ENTITY']).default('ALL'),
          content: z.string().nullish(),
          byLegalEntity: z
            .array(
              z.object({
                legalEntityIds: z.array(z.string()).min(1),
                content: z.string().nullish(),
              }),
            )
            .default([]),
        })
        .prefault({})
        .superRefine((val, ctx) => {
          if (val.scope === 'ALL' && val.byLegalEntity.length > 0) {
            ctx.addIssue({
              code: 'custom',
              message: 'This field is required',
              path: ['byLegalEntity'],
            });
          }

          if (val.scope === 'LEGAL_ENTITY' && !val.byLegalEntity.length) {
            ctx.addIssue({
              code: 'custom',
              message: 'This field is required',
              path: ['byLegalEntity'],
            });
          }

          if (val.scope === 'ALL' && val.enabled && !val.content) {
            ctx.addIssue({
              code: 'custom',
              message: 'This field is required',
              path: ['content'],
            });
          }
        }),
    })
    .prefault({}),
});

export type ISettingsConfigQuoteShareSchema = z.infer<
  typeof SettingsConfigQuoteShareSchema
>;

/**
 * The customer-facing subset of the Quote Share config — everything that drives
 * the anonymous share page. Rep-facing keys (`esign`) are omitted so they are
 * never served to unauthenticated visitors.
 */
export const AnonymousSettingsConfigQuoteShareSchema =
  SettingsConfigQuoteShareSchema.omit({ esign: true });

export type IAnonymousSettingsConfigQuoteShareSchema = z.infer<
  typeof AnonymousSettingsConfigQuoteShareSchema
>;

/**
 * Quote and Contract Settings
 */
export const QuoteAndContractSettingsConfigSchema = z.object({
  allowRetainApprovalsRoles: RoleBasedSettingConfigDefaultAdminSchema,
  allowChangingQuoteOfferingStartAndEndDate: booleanTrueSchema,
  enableEarlyRenewal: booleanTrueSchema,
  enableQuoteItemNotes: booleanFalseSchema,
  enableQuoteOfferingPriceTiers: booleanTrueSchema,
  enableBulkAddQuoteOffering: booleanFalseSchema,
  hideAutoRenewal: booleanFalseSchema,
  hideBillingSchedule: booleanFalseSchema,
  lockPriceModelUpdateOnQuote: booleanFalseSchema,
  quoteAcceptanceAllowedRoles: RoleBasedSettingConfigDefaultAllSchema,
  quoteManualAcceptanceAllowedRoles: RoleBasedSettingConfigDefaultAllSchema,
  quoteCreateAllowedRoles: RoleBasedSettingConfigDefaultAllSchema,
  accountSpecificRateCreation: RoleBasedSettingConfigDefaultAllSchema,
  accountSpecificRateAllowPercentageAdjustments: booleanTrueSchema,
  accountSpecificRateAllowTierAdjustments: booleanTrueSchema,
  accountSpecificRateAllowPriceAdjustments: booleanTrueSchema,
  quoteShareConfig: SettingsConfigQuoteShareSchema.prefault({}),
  showCommaFormattedQty: booleanTrueSchema,
  showTOCPermission: SettingsConfigTOCPermissionSchema.prefault({
    disableQuoteTermsAndConditions: false,
    allowedRoles: [],
  }),
  quoteNameEditableRoles: RoleBasedSettingConfigDefaultAllSchema,
  enableBillingAnchorAdjustment: booleanTrueSchema,
  enableUpfrontBilling: booleanFalseSchema,
  allowQuantityDropdown: booleanTrueSchema,
  enablePriceTierLockEligible: booleanFalseSchema,
});

/**
 * Quote Template Settings
 *
 * Role-based visibility for each Quote Template customization setting (the
 * toggles configured under Settings > Quote Templates). The setting *values*
 * live in a separate config (`/api/configurations/quoteTemplates`); this only
 * governs who may see/edit each toggle when customizing a quote.
 *
 * `settings` is keyed by the quote-template config field name (e.g.
 * `showPricing`). A missing key resolves to the schema defaults below
 * (`ALL_ROLES` / `EDITABLE`), so new settings are open by default.
 */
export const QuoteTemplateSettingPermissionSchema = z
  .object({
    accessScope: SettingAccessScopeEnumZ.default(
      SettingAccessScopeEnum.ALL_ROLES,
    ),
    allRolesVisibility: SettingVisibilityEnumZ.default(
      SettingVisibilityEnum.EDITABLE,
    ),
    roleVisibility: z
      .partialRecord(UserRoleEnumZ, SettingVisibilityEnumZ)
      .default({}),
  })
  .prefault({});

export const QuoteTemplateSettingsConfigSchema = z.object({
  settings: z
    .record(z.string(), QuoteTemplateSettingPermissionSchema)
    .default({}),
});

export const QuoteTemplateSettingsConfigWithKeySchema = z.object({
  key: z.literal(ExperienceConfigurationKeys.quoteTemplateSettings),
  configuration: QuoteTemplateSettingsConfigSchema.prefault({}),
});

export const TenantSettingsConfigSchema = z.object({
  debugModeRoles: RoleBasedSettingConfigDefaultAllSchema.default([]),
  enableQuoteOutputLanguageSelection: booleanFalseSchema,
});

export const TenantSettingsConfigWithKeySchema = z.object({
  key: z.literal(ExperienceConfigurationKeys.tenantSettings),
  configuration: TenantSettingsConfigSchema.prefault({}),
});

export const QuoteAndContractSettingsConfigWithKeySchema = z.object({
  key: z.literal(ExperienceConfigurationKeys.quoteAndContractSettings),
  configuration: QuoteAndContractSettingsConfigSchema.prefault({}),
});

/**
 * Quote and Contract Settings
 */
export const InvoiceSettingsConfigSchema = z.object({});

export const InvoiceSettingsConfigWithKeySchema = z.object({
  key: z.literal(ExperienceConfigurationKeys.invoiceSettings),
  configuration: InvoiceSettingsConfigSchema.prefault({}),
});

// Add new key schema here in one place ⬆️

export const ExperienceConfigurationSchema = z.discriminatedUnion('key', [
  ExperienceConfigurationBaseSchema.extend(
    AccountSettingsConfigWithKeySchema.shape,
  ),
  ExperienceConfigurationBaseSchema.extend(
    ContactSettingsConfigWithKeySchema.shape,
  ),
  ExperienceConfigurationBaseSchema.extend(
    InvoiceSettingsConfigWithKeySchema.shape,
  ),
  ExperienceConfigurationBaseSchema.extend(
    QuoteAndContractSettingsConfigWithKeySchema.shape,
  ),
  ExperienceConfigurationBaseSchema.extend(
    QuoteTemplateSettingsConfigWithKeySchema.shape,
  ),
  ExperienceConfigurationBaseSchema.extend(
    TenantSettingsConfigWithKeySchema.shape,
  ),
]);

export const ExperienceConfigurationReqSchema = z.discriminatedUnion('key', [
  AccountSettingsConfigWithKeySchema,
  ContactSettingsConfigWithKeySchema,
  InvoiceSettingsConfigWithKeySchema,
  QuoteAndContractSettingsConfigWithKeySchema,
  QuoteTemplateSettingsConfigWithKeySchema,
  TenantSettingsConfigWithKeySchema,
  // Add new request key schema here  ⬆️
]);

export const ExperienceConfigurationRequestSchema =
  ExperienceConfigurationReqSchema;

export type ExperienceConfigurationRequest = z.infer<
  typeof ExperienceConfigurationRequestSchema
>;

export type ExperienceConfiguration = z.infer<
  typeof ExperienceConfigurationSchema
>;

export const ExperienceConfigurationResponseSchema = z.object({
  accountSettings: AccountSettingsConfigSchema.prefault({}),
  contactSettings: ContactSettingsConfigSchema.prefault({}),
  invoiceSettings: InvoiceSettingsConfigSchema.prefault({}),
  quoteAndContractSettings: QuoteAndContractSettingsConfigSchema.prefault({}),
  quoteTemplateSettings: QuoteTemplateSettingsConfigSchema.prefault({}),
  tenantSettings: TenantSettingsConfigSchema.prefault({}),
});
export type ExperienceConfigurationResponse = z.infer<
  typeof ExperienceConfigurationResponseSchema
>;

/**
 * Anonymous Schema
 * All properties here can be accessed from unauthenticated pages, such as invoice/quote share and deal room
 *
 * Ensure any new properties are also added to `ExperienceConfigurationKeyValueUnionSchema`
 */
export const ExperienceConfigurationAnonymousResponseSchema = z.object({
  accountSettings: AccountSettingsConfigSchema.prefault({}),
  contactSettings: ContactSettingsConfigSchema.prefault({}),
  invoiceSettings: InvoiceSettingsConfigSchema.prefault({}),
  quoteAndContractSettings: z
    .object({
      quoteShareConfig: AnonymousSettingsConfigQuoteShareSchema.prefault({}),
    })
    .prefault({}),
  tenantSettings: TenantSettingsConfigSchema.prefault({}),
});
export type ExperienceConfigurationAnonymousResponse = z.infer<
  typeof ExperienceConfigurationAnonymousResponseSchema
>;

export const ExperienceConfigurationCreateAllSchema = z.object({
  count: z.number(),
});

export type RoleBasedSettingConfig = z.infer<
  typeof RoleBasedSettingConfigDefaultAllSchema
>;
export type AccountSettingsConfigWithKey = z.infer<
  typeof AccountSettingsConfigWithKeySchema
>;
export type ContactSettingsConfigWithKey = z.infer<
  typeof ContactSettingsConfigWithKeySchema
>;
export type InvoiceSettingsConfigWithKey = z.infer<
  typeof InvoiceSettingsConfigWithKeySchema
>;
export type QuoteAndContractSettingsConfigWithKey = z.infer<
  typeof QuoteAndContractSettingsConfigWithKeySchema
>;
export type AccountSettingsConfig = z.infer<typeof AccountSettingsConfigSchema>;
export type ContactSettingsConfig = z.infer<typeof ContactSettingsConfigSchema>;
export type InvoiceSettingsConfig = z.infer<typeof InvoiceSettingsConfigSchema>;
export type QuoteAndContractSettingsConfig = z.infer<
  typeof QuoteAndContractSettingsConfigSchema
>;
export type QuoteTemplateSettingsConfig = z.infer<
  typeof QuoteTemplateSettingsConfigSchema
>;
export type QuoteTemplateSettingsConfigWithKey = z.infer<
  typeof QuoteTemplateSettingsConfigWithKeySchema
>;
export type QuoteTemplateSettingPermission = z.infer<
  typeof QuoteTemplateSettingPermissionSchema
>;
export type TenantSettingsConfigWithKey = z.infer<
  typeof TenantSettingsConfigWithKeySchema
>;

export const ExperienceConfigurationKeyValueUnionSchema = z.union([
  z.object({
    key: z.literal(ExperienceConfigurationKeys.accountSettings),
    property: AccountSettingsConfigSchema.keyof(),
  }),
  z.object({
    key: z.literal(ExperienceConfigurationKeys.contactSettings),
    property: ContactSettingsConfigSchema.keyof(),
  }),
  z.object({
    key: z.literal(ExperienceConfigurationKeys.invoiceSettings),
    property: InvoiceSettingsConfigSchema.keyof(),
  }),
  z.object({
    key: z.literal(ExperienceConfigurationKeys.quoteAndContractSettings),
    property: QuoteAndContractSettingsConfigSchema.keyof(),
  }),
  z.object({
    key: z.literal(ExperienceConfigurationKeys.quoteTemplateSettings),
    property: QuoteTemplateSettingsConfigSchema.keyof(),
  }),
  z.object({
    key: z.literal(ExperienceConfigurationKeys.tenantSettings),
    property: TenantSettingsConfigSchema.keyof(),
  }),
]);

export type ExperienceConfigurationKeyValueUnion = z.infer<
  typeof ExperienceConfigurationKeyValueUnionSchema
>;
